/**
 * What each number is called and what it means (docs/analytics.md §1), in plain words, per kind of
 * business: every metric on every screen takes its label and its one-sentence tooltip from here,
 * so a number reads the same on Home, Analytics and an item's page. The headline tile follows the
 * vocabulary pack (a gym counts classes completed, a church who watched live).
 *
 * Pure: data and lookups; the pack is passed in (type import only), so the unit tests run it under
 * plain Node.
 */

import type { Pack } from "../vocabulary/vocabulary";

export type MetricId =
  | "plays" | "engagedPlays" | "engagedRate" | "watchSeconds" | "viewers" | "completed" | "completionRate" | "averageWatch"
  | "retention" | "watchingNow" | "playsLastHour" | "peak" | "liveViewers" | "replayViewers" | "liveWatch" | "replayWatch"
  | "newViewers" | "returningViewers"
  | "subscribers" | "started" | "ended" | "sales" | "estimatedSales" | "comps" | "codesRedeemed"
  | "recipesWatched";

export interface Definition {
  label: string;
  /** One sentence, said where the number is. */
  tooltip: string;
}

const BASE: Record<MetricId, Definition> = {
  plays: { label: "Plays", tooltip: "Times someone pressed play and the video started." },
  engagedPlays: { label: "Engaged plays", tooltip: "Plays that lasted at least 30 seconds." },
  engagedRate: { label: "Engaged", tooltip: "Share of plays that lasted at least 30 seconds." },
  watchSeconds: { label: "Watch time", tooltip: "Total time people spent actually watching, not counting pauses, skipping or loading." },
  viewers: { label: "Viewers", tooltip: "Different devices that watched, counted without knowing who they are; a device is counted afresh each month." },
  completed: { label: "Completed", tooltip: "Plays that reached 90% of the video or its end." },
  completionRate: { label: "Completion", tooltip: "Share of plays that reached 90% of the video or its end." },
  averageWatch: { label: "Average watch", tooltip: "How long a typical play lasts: watch time divided by plays." },
  retention: { label: "How far people watch", tooltip: "For each point in the video, the share of plays that watched it." },
  watchingNow: { label: "Watching now", tooltip: "Devices playing something in the last two and a half minutes." },
  playsLastHour: { label: "Plays in the last hour", tooltip: "Plays that started in the last 60 minutes, minute by minute." },
  peak: { label: "Peak at once", tooltip: "The most people watching live at the same moment." },
  liveViewers: { label: "Watched live", tooltip: "Different devices that watched while it was live." },
  replayViewers: { label: "Watched the replay", tooltip: "Different devices that watched the replay afterwards." },
  liveWatch: { label: "Live watch time", tooltip: "Total time spent watching while it was live." },
  replayWatch: { label: "Replay watch time", tooltip: "Total time spent watching the replay." },
  newViewers: { label: "New viewers", tooltip: "Devices that watched for the first time this month in this period. Devices are counted afresh each month, so this resets on the 1st." },
  returningViewers: { label: "Returning viewers", tooltip: "Devices that had already watched on an earlier day this month. Devices are counted afresh each month." },
  subscribers: { label: "Members", tooltip: "Subscriptions active today." },
  started: { label: "New members", tooltip: "Subscriptions that started in this period." },
  ended: { label: "Members lost", tooltip: "Subscriptions that ended in this period." },
  sales: { label: "Sales", tooltip: "Rentals and purchases made in this period." },
  estimatedSales: { label: "Estimated sales", tooltip: "Sales times today's store prices, before store fees and taxes: an estimate, not a payout." },
  comps: { label: "Free access given", tooltip: "Access you gave without a purchase: comps, guest lists and redeemed codes." },
  codesRedeemed: { label: "Codes redeemed", tooltip: "Ticket and access codes viewers redeemed in this period." },
  recipesWatched: { label: "Watched", tooltip: "Plays that lasted at least 30 seconds." },
};

const lower = (s: string) => s.toLowerCase();

/** A metric's label and tooltip in this business's words. */
export function definition(id: MetricId, pack: Pack): Definition {
  const base = BASE[id];
  const items = pack.nouns.title.many;
  const item = lower(pack.nouns.title.one);
  switch (id) {
    case "completed":
      return pack.vertical === "fitness" || pack.vertical === "education"
        ? { label: `${items} completed`, tooltip: `${pack.nouns.title.one} plays that reached 90% of the video or its end.` }
        : base;
    case "recipesWatched":
      return { label: `${items} watched`, tooltip: `Plays of a ${item} that lasted at least 30 seconds.` };
    case "liveViewers":
      return { ...base, tooltip: `Different devices that watched a ${lower(pack.nouns.liveEvent.one)} while it was live.` };
    default:
      return base;
  }
}

/** Where a headline's figure comes from: the summary's totals, or the live events of the range. */
export type HeadlineSource = { from: "totals"; key: "plays" | "engagedPlays" | "watchSeconds" | "viewers" | "completed" } | { from: "live"; key: "liveViewers" | "peak" };

export interface Headline {
  id: MetricId;
  source: HeadlineSource;
}

/** The one number that matters most to this kind of business. */
export function headline(pack: Pack): Headline {
  switch (pack.vertical) {
    case "fitness":
    case "education": return { id: "completed", source: { from: "totals", key: "completed" } };
    case "faith":
    case "music":
    case "theatre":
    case "events": return { id: "liveViewers", source: { from: "live", key: "liveViewers" } };
    case "sport": return { id: "peak", source: { from: "live", key: "peak" } };
    case "kids": return { id: "plays", source: { from: "totals", key: "plays" } };
    case "food": return { id: "recipesWatched", source: { from: "totals", key: "engagedPlays" } };
    default: return { id: "watchSeconds", source: { from: "totals", key: "watchSeconds" } };
  }
}

/**
 * The four "Last 28 days" tiles: the headline first, then plays, watch time and viewers; when the
 * headline is one of those, the next useful one (completed, then engaged plays) fills its place.
 */
export function kpiTiles(pack: Pack): MetricId[] {
  const first = headline(pack).id;
  const rest = (["plays", "watchSeconds", "viewers", "completed", "engagedPlays"] as MetricId[]).filter((id) => id !== first);
  return [first, ...rest].slice(0, 4);
}

/**
 * Home's four range tiles, in the order a glance reads them: watch time, plays and viewers, then the
 * vertical's headline; when the headline is one of those three, the next useful one (completed, then
 * engaged plays) takes the fourth place.
 */
export function homeKpiTiles(pack: Pack): MetricId[] {
  const base: MetricId[] = ["watchSeconds", "plays", "viewers"];
  const first = headline(pack).id;
  const fourth = base.includes(first) ? (["completed", "engagedPlays"] as MetricId[]).find((id) => id !== first)! : first;
  return [...base, fourth];
}

const PLATFORMS: Record<string, string> = {
  ios: "iPhone",
  ipados: "iPad",
  tvos: "Apple TV",
  android: "Android phones and tablets",
  androidtv: "Android TV",
  firetv: "Fire TV",
  other: "Other",
};

/** A platform key in plain words. */
export function platformLabel(key: string): string {
  return PLATFORMS[key] ?? key;
}

const regions = new Intl.DisplayNames(["en"], { type: "region" });

/** A country code ("GB") as its name; "other" (fewer than 5 viewers each) as Other. */
export function countryLabel(key: string): string {
  if (key === "other") return "Other";
  if (!/^[A-Z]{2}$/i.test(key)) return key === "XX" || !key ? "Unknown" : key;
  try { return regions.of(key.toUpperCase()) ?? key; } catch { return key; }
}

const MODES: Record<string, string> = { vod: "On demand", live: "Live", replay: "Replay", offline: "Downloaded" };

/** How something was watched, in plain words. */
export function modeLabel(mode: string): string {
  return MODES[mode] ?? mode;
}
